import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

// Create axios instance - using Authorization header for auth (works with wildcard CORS)
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on auth page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  exchangeSession: (sessionId) => 
    api.get('/auth/session', { headers: { 'X-Session-ID': sessionId } }),
};

// Project APIs
export const projectAPI = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  boost: (id, data) => api.post(`/projects/${id}/boost`, data),
  addComment: (id, data) => api.post(`/projects/${id}/comments`, data),
  getComments: (id) => api.get(`/projects/${id}/comments`),
};

// Payment APIs
export const paymentAPI = {
  createSession: (data) => api.post('/payments/create-session', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// User APIs
export const userAPI = {
  getProfile: (id) => api.get(`/users/${id}`),
  getLeaderboard: (type) => api.get('/leaderboard', { params: { type } }),
  updateProfile: (data) => api.put('/users/me', data),
  search: (q) => api.get('/users/search', { params: { q } }),
};

// Admin APIs
export const adminAPI = {
  getAnalytics: () => api.get('/admin/analytics'),
  toggleSmartPick: (id, data) => api.post(`/admin/projects/${id}/smart-pick`, data),
  flagProject: (id, data) => api.post(`/admin/projects/${id}/flag`, data),
  getFlagged: () => api.get('/admin/flagged'),
  updateUserTrust: (id, trustLevel) =>
    api.post(`/admin/users/${id}/trust`, null, { params: { trust_level: trustLevel } }),
  // Site Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // Pages (legal/content)
  getPages: () => api.get('/admin/pages'),
  getPage: (slug) => api.get(`/admin/pages/${slug}`),
  updatePage: (slug, data) => api.put(`/admin/pages/${slug}`, data),
  // System status
  getSystemStatus: () => api.get('/admin/system-status'),
};

// Public Site APIs
export const siteAPI = {
  getSettings: () => api.get('/site/settings'),
  getPage: (slug) => api.get(`/pages/${slug}`),
};

// Upload APIs
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Category APIs
export const categoryAPI = {
  list: () => api.get('/categories'),
};

// Follow APIs
export const followAPI = {
  follow: (userId) => api.post(`/users/${userId}/follow`),
  unfollow: (userId) => api.delete(`/users/${userId}/follow`),
  getFollowers: (userId, params) => api.get(`/users/${userId}/followers`, { params }),
  getFollowing: (userId, params) => api.get(`/users/${userId}/following`, { params }),
  getFollowStatus: (userId) => api.get(`/users/${userId}/follow-status`),
  getSuggestions: () => api.get('/users/suggestions'),
};

// Message APIs
export const messageAPI = {
  getConversations: (params) => api.get('/conversations', { params }),
  getConversation: (id, params) => api.get(`/conversations/${id}`, { params }),
  createConversation: (targetUserId) =>
    api.post('/conversations', null, { params: { target_user_id: targetUserId } }),
  sendMessage: (conversationId, data) =>
    api.post(`/conversations/${conversationId}/messages`, data),
  markRead: (conversationId) => api.put(`/conversations/${conversationId}/read`),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// User Update APIs
export const updateAPI = {
  create: (data) => api.post('/updates', data),
  getUserUpdates: (userId, params) => api.get(`/users/${userId}/updates`, { params }),
  delete: (updateId) => api.delete(`/updates/${updateId}`),
};

// Notification APIs
export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  markAllRead: () => api.put('/notifications/read'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Feed APIs
export const feedAPI = {
  getFollowingFeed: (params) => api.get('/feed/following', { params }),
};

// Auth refresh
export const authRefreshAPI = {
  refresh: () => api.post('/auth/refresh'),
};

export default api;
